import React, { useEffect, useMemo, useState, useCallback } from "react";
import "./WorldRatedDetail.css";

export default function WorldRatedDetail({ hotel, onClose }) {
  const [activeIndex, setActiveIndex] = useState(0);

  // POV (my shots)
  const povImages = useMemo(() => {
    if (!hotel?.pov || !Array.isArray(hotel.pov)) return [];
    return hotel.pov
      .filter(Boolean)
      .map(
        (fname) =>
          `https://lvltravel.s3.us-east-2.amazonaws.com/hotels/${fname}`
      );
  }, [hotel]);

  // Official property images (full URLs)
  const officialImgs = useMemo(() => {
    if (!hotel?.images) return [];
    if (Array.isArray(hotel.images)) return hotel.images.filter(Boolean);
    if (typeof hotel.images === "string") return [hotel.images];
    return [];
  }, [hotel]);

  const activeSrc = povImages[activeIndex] || "";

  const handleThumbClick = useCallback(
    (idx) => {
      if (idx === activeIndex) return;
      setActiveIndex(idx);
    },
    [activeIndex]
  );

  // rating stars
  const stars = useMemo(() => {
    const n = parseInt(hotel?.rating, 10);
    const safe = Number.isNaN(n) ? 0 : Math.max(0, Math.min(5, n));
    return (
      <div className="wrd-stars" aria-label={`Rating ${safe} of 5`}>
        <span className="wrd-stars__full">{"★".repeat(safe)}</span>
        <span className="wrd-stars__dim">{"★".repeat(5 - safe)}</span>
      </div>
    );
  }, [hotel]);

  /* =========================
     LIGHTBOX STATE
     ========================= */
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxGroup, setLightboxGroup] = useState("pov"); // "pov" | "official"
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const currentLightboxArray =
    lightboxGroup === "official" ? officialImgs : povImages;

  const openLightbox = useCallback((group, index) => {
    setLightboxGroup(group);
    setLightboxIndex(index);
    setLightboxOpen(true);
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
  }, []);

  const showPrev = useCallback(() => {
    setLightboxIndex((i) => {
      if (!currentLightboxArray.length) return i;
      return (i - 1 + currentLightboxArray.length) % currentLightboxArray.length;
    });
  }, [currentLightboxArray.length]);

  const showNext = useCallback(() => {
    setLightboxIndex((i) => {
      if (!currentLightboxArray.length) return i;
      return (i + 1) % currentLightboxArray.length;
    });
  }, [currentLightboxArray.length]);

  // keyboard nav for lightbox
  useEffect(() => {
    if (!lightboxOpen) return;
    const handler = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowLeft") showPrev();
      if (e.key === "ArrowRight") showNext();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [lightboxOpen, closeLightbox, showPrev, showNext]);

  // helper to render hero img block for mobile POV
  const renderHeroImageMobile = () => (
    <div className="wrd-hero-shell-mobile">
      <div className="wrd-hero-stage">
        {activeSrc ? (
          <img
            key={activeSrc}
            src={activeSrc}
            alt={`${hotel?.name || "Hotel"} POV`}
            className="wrd-hero-img-mobile wrd-hero-img--visible"
            onClick={() => {
              openLightbox("pov", activeIndex);
            }}
          />
        ) : (
          <div className="wrd-hero-fallback-mobile">
            No personal images yet
          </div>
        )}
      </div>
    </div>
  );

  // ---- NEW: does this hotel have POV images?
  const hasPOV = povImages.length > 0;

  return (
    <>
      <div className="wrd-overlay">
        <div className="wrd-panel wrd-reveal">
          {/* Close */}
          <button
            className="wrd-close"
            type="button"
            onClick={onClose}
            aria-label="Close hotel view"
          >
            ✕
          </button>

          {/* wrd-grid class now branches based on hasPOV */}
          <div
            className={`wrd-grid ${
              hasPOV ? "wrd-grid--haspov" : "wrd-grid--nopov"
            }`}
          >
            {/* LEFT COLUMN */}
            <aside className="wrd-left">
              <header className="wrd-header">
                {hotel?.continent && (
                  <div className="wrd-overline">{hotel.continent}</div>
                )}

                <h1 className="wrd-name">{hotel?.name || "Untitled Hotel"}</h1>

                <div className="wrd-locationblock">
                  <div className="wrd-where">
                    {[hotel?.city, hotel?.country]
                      .filter(Boolean)
                      .join(", ") || "—"}
                  </div>
                  {stars}
                </div>

                {hotel?.description && (
                  <p className="wrd-desc">{hotel.description}</p>
                )}

                {hotel?.myPersonalReview?.trim() && (
                  <div className="wrd-review">
                    <div className="wrd-review-label">My Notes</div>
                    <div className="wrd-review-body">
                      {hotel.myPersonalReview}
                    </div>
                  </div>
                )}
              </header>

              {/* POV BLOCK (MOBILE ONLY) */}
              {hasPOV && (
                <section className="wrd-pov-mobile">
                  <div className="wrd-pov-heading-mobile">
                    <span>My Perspective</span>
                    <span className="dash">—</span>
                    <span>LVL</span>
                  </div>

                  {renderHeroImageMobile()}

                  <div className="wrd-pov-heading-mobile">
                    See more through my perspective
                  </div>

                  {povImages.length > 1 && (
                    <div className="wrd-strip-shell-mobile">
                      <div className="wrd-strip-mobile">
                        {povImages.map((src, i) => (
                          <button
                            key={src + i}
                            type="button"
                            className={`wrd-thumb-btn ${
                              i === activeIndex
                                ? "wrd-thumb-btn--active"
                                : ""
                            }`}
                            onClick={() => {
                              handleThumbClick(i);
                              openLightbox("pov", i);
                            }}
                            aria-label={`Show photo ${i + 1}`}
                          >
                            <img
                              src={src}
                              alt={`POV ${i + 1}`}
                              className="wrd-thumb-img"
                              loading="lazy"
                            />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </section>
              )}

              {/* OFFICIAL IMAGES */}
              {officialImgs.length > 0 && (
                <section className="wrd-official">
                  <div className="wrd-official-head">
                    <div className="wrd-official-label">Hotel Imagery</div>
                    <div className="wrd-official-disclaimer">
                      These images are provided by the property / outside
                      sources. They are not shot by LVL.
                    </div>
                  </div>

                  <div className="wrd-official-grid">
                    {officialImgs.map((src, i) => (
                      <button
                        key={src + i}
                        type="button"
                        className="wrd-official-tile"
                        onClick={() => openLightbox("official", i)}
                        aria-label={`Open hotel image ${i + 1}`}
                      >
                        <img
                          src={src}
                          alt={`${hotel?.name || "Hotel"} — official ${
                            i + 1
                          }`}
                          loading="lazy"
                          className="wrd-official-img"
                        />
                      </button>
                    ))}
                  </div>
                </section>
              )}
            </aside>

            {/* RIGHT COLUMN (DESKTOP POV VIEWER) */}
            {hasPOV && (
              <section className="wrd-right">
                <div className="wrd-pov-block-desktop">
                  <div className="wrd-pov-heading-desktop">
                    <span>My Perspective</span>
                    <span className="dash">—</span>
                    <span>LVL</span>
                  </div>

                  <div className="wrd-pov-bodyrow">
                    <div className="wrd-pov-mainpane">
                      <div className="wrd-hero-stage-desktop">
                        {activeSrc ? (
                          <img
                            key={activeSrc}
                            src={activeSrc}
                            alt={`${hotel?.name || "Hotel"} POV`}
                            className="wrd-hero-img-desktop wrd-hero-img--visible"
                            onClick={() => {
                              openLightbox("pov", activeIndex);
                            }}
                          />
                        ) : (
                          <div className="wrd-hero-fallback">
                            No personal images yet
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="wrd-pov-railpane">
                      <div className="wrd-pov-railscroll">
                        {povImages.map((src, i) => (
                          <button
                            key={src + i}
                            type="button"
                            className={`wrd-thumb-btn ${
                              i === activeIndex
                                ? "wrd-thumb-btn--active"
                                : ""
                            }`}
                            onClick={() => {
                              handleThumbClick(i);
                            }}
                            aria-label={`Show photo ${i + 1}`}
                          >
                            <img
                              src={src}
                              alt={`POV ${i + 1}`}
                              className="wrd-thumb-img-rail"
                              loading="lazy"
                            />
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            )}
          </div>
        </div>
      </div>

      {/* LIGHTBOX */}
      {lightboxOpen && currentLightboxArray.length > 0 && (
        <div
          className="wrd-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label="Image viewer"
        >
          <div
            className="wrd-lightbox__bg"
            onClick={closeLightbox}
            aria-hidden="true"
          />

          <div className="wrd-lightbox__inner">
            <button
              className="wrd-lightbox__close"
              type="button"
              onClick={closeLightbox}
              aria-label="Close image viewer"
            >
              ✕
            </button>

            {currentLightboxArray.length > 1 && (
              <>
                <button
                  className="wrd-lightbox__nav wrd-lightbox__nav--prev"
                  type="button"
                  onClick={showPrev}
                  aria-label="Previous image"
                >
                  ‹
                </button>
                <button
                  className="wrd-lightbox__nav wrd-lightbox__nav--next"
                  type="button"
                  onClick={showNext}
                  aria-label="Next image"
                >
                  ›
                </button>
              </>
            )}

            <div className="wrd-lightbox__stage">
              <img
                src={currentLightboxArray[lightboxIndex]}
                alt={`Enlarged ${lightboxGroup} view ${
                  lightboxIndex + 1
                } of ${currentLightboxArray.length}`}
                className="wrd-lightbox__img"
              />
            </div>

            <div className="wrd-lightbox__meta">
              <div className="wrd-lightbox__meta-line">
                <span className="wrd-lightbox__meta-group">
                  {lightboxGroup === "pov"
                    ? "My Perspective"
                    : "Hotel Imagery"}
                </span>
                <span className="wrd-lightbox__meta-dot">•</span>
                <span className="wrd-lightbox__meta-count">
                  {lightboxIndex + 1} / {currentLightboxArray.length}
                </span>
              </div>

              {hotel?.name && (
                <div className="wrd-lightbox__hotelname">{hotel.name}</div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
